import { Node as TiptapNode } from "@tiptap/pm/model";
import { EditorState } from "@tiptap/pm/state";
import {
  NodeViewContent,
  NodeViewContentProps,
  NodeViewWrapper,
  NodeViewWrapperProps,
} from "@tiptap/react";

import useTranslatorVisibility from "@/assignments/useTranslatorVisibility/useTranslatorVisibility";
import { ContentType } from "@/view/constants/contentTypes";
import useTranslatorControlsContext from "@/view/utils/useTranslatorControlsContext";
import { getDynamicData } from "../../extensions/DataProvider";
import TranslateDrawer from "../TranslateDrawer";
import {
  borderCss,
  translatedContentLayout,
  translationModeLayout,
} from "./NodeWithTranslation.style";
import TranslateContentButton from "./TranslateContentButton";
import { isDirectChild } from "./utils";

interface NodeWithTranslationProps {
  state: EditorState;
  node: TiptapNode;
  props?: NodeViewContentProps;
  wrapperProps?: NodeViewWrapperProps;
  disableTranslation?: boolean;
}

const NodeWithTranslation = ({
  state,
  node,
  props,
  wrapperProps,
  disableTranslation = false,
}: NodeWithTranslationProps) => {
  const identifier = `${node.attrs["data-identifier"] || ""}`;
  const text = node.textContent.trim();
  const isFirstLevelText = isDirectChild(state.doc, node) && !!text;
  const { activeTranslatedItem, setActiveTranslatedItem, isTranslateMode } =
    useTranslatorControlsContext();

  const { content, language } = getDynamicData(state) || {};

  const { isTranslationActionVisible, supportedLanguages } =
    useTranslatorVisibility({
      content: {
        content_type: content?.content_type as ContentType,
        data: content?.data,
      },
      language,
    });
  const handleActiveTranslatedItem = () => {
    const newActive =
      activeTranslatedItem !== identifier ? identifier : undefined;

    setActiveTranslatedItem(newActive);

    if (newActive) {
      requestAnimationFrame(() => {
        const el = document.getElementById(`translate-drawer-${identifier}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    }
  };

  const isActiveTranslatedItem =
    !!identifier && activeTranslatedItem === identifier;
  const shouldShowTranslateButton =
    isTranslateMode &&
    isFirstLevelText &&
    isTranslationActionVisible &&
    !disableTranslation;
  const shouldShowTranslateDrawer =
    shouldShowTranslateButton && isActiveTranslatedItem;
  const layout = shouldShowTranslateButton ? translationModeLayout : {};
  const translatedContentMargin = shouldShowTranslateButton
    ? translatedContentLayout
    : {};
  const activeBorderCss =
    shouldShowTranslateButton && isActiveTranslatedItem ? borderCss : {};

  return (
    <NodeViewWrapper css={[layout, activeBorderCss]} {...wrapperProps}>
      {shouldShowTranslateButton && (
        <TranslateContentButton
          state={state}
          identifier={identifier}
          onClick={handleActiveTranslatedItem}
          node={node}
        />
      )}
      <NodeViewContent {...props} css={translatedContentMargin} />
      {shouldShowTranslateDrawer && (
        <TranslateDrawer
          state={state}
          identifier={identifier}
          onClose={handleActiveTranslatedItem}
          text={text}
          supportedLanguages={supportedLanguages}
          node={node}
        />
      )}
    </NodeViewWrapper>
  );
};

export default NodeWithTranslation;
